import { ValidationErrorResponse } from '@wyz/types';
import {
  Button,
  Card,
  Col,
  Flex,
  FormContainer,
  FormFeedback,
  Icon,
  IconButton,
  Row,
  TabsFactory,
  Text,
  Title,
} from '@wyz/ui';
import clsx from 'clsx';
import { isNil } from 'lodash-es';
import React, { useCallback, useMemo, useState } from 'react';
import {
  DefaultValues,
  FieldValues,
  Resolver,
  useFieldArray,
  useForm,
  useFormContext,
  UseFormGetFieldState,
  useWatch,
} from 'react-hook-form';
import { Link } from 'react-router';
import {
  useManageBrands,
  useManageCriteria,
  useManageSegments,
} from '~/common/api';
import {
  CheckboxField,
  CriteriaBuilder,
  MatchedCustomersTable,
  SelectField,
  TextField,
} from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import { useMyCustomers, useValidationForm } from '~/common/hooks';
import { FEATURE_CONTEXT } from '~/common/schemas';
import {
  CommonErrorCodesEnumType,
  IBrand,
  TranslationKeys,
} from '~/common/types';
import {
  AlphabeticalListSearchInput,
  BrandAlphabeticalList,
  BrandDraggables,
  DesadvGlobalCheckbox,
  SelectBrandEanConfiguration,
} from '~/connect/components';
import { BrandTemplateHelpers } from '~/connect/helpers';
import { useScrollToNewItem } from '~/connect/hooks';
import { IBrandTemplateBrand, ICriteria } from '~/connect/types';

type StandardVariantConfig = {
  variant: 'STANDARD';
  featureContext: typeof FEATURE_CONTEXT.BRAND_TEMPLATE;
  cancelTo: string;
};

type ScopedVariantConfig = {
  variant: 'SCOPED';
  featureContext:
    | typeof FEATURE_CONTEXT.REDISTRIBUTION_BRAND_TEMPLATE
    | typeof FEATURE_CONTEXT.QUOTE_BRAND_TEMPLATE;
  cancelTo: string;
  customerScope: {
    minLevel?: number;
    filterOption: (customerLevel: number) => boolean;
  };
};

type BrandTemplateFormVariantConfig =
  | StandardVariantConfig
  | ScopedVariantConfig;

type BrandTemplateFormSubmission<TForm extends FieldValues> = {
  isLoading?: boolean;
  errors?: ValidationErrorResponse<CommonErrorCodesEnumType> | null;
  onSubmit?: (
    formData: TForm,
    criteria: ICriteria[],
    formState: { criteria: ReturnType<UseFormGetFieldState<TForm>> },
  ) => void;
};

type BrandTemplateFormBaseProps<TForm extends FieldValues> = {
  config: BrandTemplateFormVariantConfig;
  resolver: Resolver<TForm>;
  defaultValues: Partial<TForm>;
  mode?: 'CREATE' | 'UPDATE' | 'READ_ONLY';
  submission?: BrandTemplateFormSubmission<TForm>;
};

type BrandField = {
  publicKey: string;
  name: string;
  segment: { publicKey: string };
  brandEanSpecification?: IBrandTemplateBrand['brandEanSpecification'];
  desadv?: boolean | null;
  recommended?: boolean;
};

const Tabs = TabsFactory<BrandTemplateHelpers.BrandTemplateTabValue>();

export function BrandTemplateFormBase<TForm extends FieldValues>({
  config,
  resolver,
  defaultValues,
  mode = 'CREATE',
  submission,
}: BrandTemplateFormBaseProps<TForm>) {
  const { t } = useI18n();

  const methods = useForm<TForm>({
    defaultValues: defaultValues as DefaultValues<TForm>,
    resolver,
  });

  useValidationForm({
    methods,
    errors: submission?.errors?.errors,
    t,
  });

  const manageCriteria = useManageCriteria({ getAll: { enabled: true } });

  const handleSubmit = (data: TForm) => {
    submission?.onSubmit?.(data, manageCriteria.getAll.data?.data ?? [], {
      criteria: methods.getFieldState('criteria' as never),
    });
  };

  return (
    <FormContainer
      methods={methods}
      submitHandler={handleSubmit}
      formConfiguration={{
        mode: mode === 'READ_ONLY' ? 'READ_ONLY' : 'WRITE',
      }}
    >
      <GeneralInformationCard config={config} />
      <CriteriaCard config={config} />
      <BrandsConfigurationCard config={config} />
      {mode !== 'READ_ONLY' && (
        <Card className='mb-section'>
          <Card.Body>
            <Flex className='space-x-4' justifyContent='between'>
              <Button
                id='cancel'
                as={Link}
                variant='secondary'
                type='button'
                to={config.cancelTo}
              >
                <Trans i18nKey='app.common.button.cancel' />
              </Button>
              <Button
                id='submit'
                variant='primary'
                type='submit'
                disabled={submission?.isLoading}
              >
                <Trans
                  i18nKey={
                    mode === 'CREATE'
                      ? 'app.brand_template.form.buttons.create'
                      : 'app.brand_template.form.buttons.update'
                  }
                />
              </Button>
            </Flex>
          </Card.Body>
        </Card>
      )}
    </FormContainer>
  );
}

type CardProps = {
  config: BrandTemplateFormVariantConfig;
};

function GeneralInformationCard({ config }: CardProps) {
  return (
    <Card className='mb-section'>
      <Card.Header>
        <Title heading='lg' color='tertiary' weight='bold'>
          <Icon icon='post' width='1.5rem' height='1.5rem' />
          <span className='ms-2'>
            <Trans i18nKey='app.common.general_information' />
          </span>
        </Title>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col xs='3'>
            <TextField
              required
              name='name'
              label={<Trans i18nKey='app.common.model_name' />}
            />
          </Col>
          {config.variant === 'SCOPED' && (
            <Col xs='3'>
              <CheckboxField
                name='default'
                label={
                  <Trans i18nKey='app.brand_template.form.default.label' />
                }
              />
            </Col>
          )}
        </Row>
      </Card.Body>
    </Card>
  );
}

function CriteriaCard({ config }: CardProps) {
  return (
    <Card className='mb-section'>
      <Card.Header className='pb-0'>
        <Flex justifyContent='between'>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='clip-board' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.common.criteria' />
            </span>
          </Title>
        </Flex>
      </Card.Header>
      <Card.Body>
        {config.variant === 'STANDARD' ? (
          <StandardCriteriaBody config={config} />
        ) : (
          <ScopedCriteriaBody config={config} />
        )}
      </Card.Body>
    </Card>
  );
}

function StandardCriteriaBody({ config }: { config: StandardVariantConfig }) {
  return (
    <CriteriaBuilder name={'criteria'}>
      <Row className='mt-4'>
        <Col md={6}>
          <MatchedCustomersTable
            featureContext={config.featureContext}
            name={'criteria'}
          />
        </Col>
      </Row>
    </CriteriaBuilder>
  );
}

function ScopedCriteriaBody({ config }: { config: ScopedVariantConfig }) {
  const { control } = useFormContext();
  const getCustomersByUser = useMyCustomers({
    minLevel: config.customerScope.minLevel,
  });
  const watchedCustomerIds = useWatch({
    control,
    name: 'customerScope',
  }) as string[] | undefined;

  return (
    <>
      <Col md={6}>
        <SelectField<true>
          name='customerScope'
          label={<Trans i18nKey='app.common.customer_scope.label.source' />}
          options={
            getCustomersByUser.data?.data
              .filter((item) => config.customerScope.filterOption(item.level))
              .map((item) => ({
                label: item.name,
                value: item.id,
              })) || []
          }
          isMulti={true}
          maxShown={Infinity}
          required
          isLoading={getCustomersByUser.isLoading}
        />
      </Col>
      <CriteriaBuilder name={'criteria'}>
        <Row className='mt-4'>
          <Col md={6}>
            <MatchedCustomersTable
              featureContext={config.featureContext}
              name={'criteria'}
              matchUserContext
              queryOverrideParams={{
                parentIds: watchedCustomerIds,
              }}
            />
          </Col>
        </Row>
      </CriteriaBuilder>
    </>
  );
}

function BrandsConfigurationCard({ config }: CardProps) {
  const { t } = useI18n();
  const methods = useFormContext<FieldValues>();

  const [searchQuery, setSearchQuery] = useState<string>('');

  const { fields, insert, remove, append, update } = useFieldArray<
    FieldValues,
    'brands',
    'key'
  >({
    control: methods.control,
    name: 'brands',
    keyName: 'key',
  });

  const brandFields = fields as unknown as Array<BrandField & { key: string }>;

  const handleOnAdd = (brand: IBrand) => {
    if (config.variant === 'STANDARD') {
      append({
        publicKey: brand.publicKey,
        name: brand.name,
        brandEanSpecification: undefined,
        desadv: false,
        recommended: false,
        segment: brand.segment,
      });
    } else {
      append({
        publicKey: brand.publicKey,
        name: brand.name,
        segment: brand.segment,
      });
    }
  };

  const handleOnRemove = (publicKey: string) => {
    const index = brandFields.findIndex(
      (field) => field.publicKey === publicKey,
    );
    remove(index);
  };

  const handleOnMove = useCallback(
    (from: number, to: number) => {
      const value = fields[from];
      remove(from);
      insert(to, value);
    },
    [fields, remove, insert],
  );

  const segments = useManageSegments({ list: { enabled: true } });
  const brands = useManageBrands({ list: { enabled: true } });
  const brandsByLetterByTab = useMemo(
    () =>
      BrandTemplateHelpers.groupByLetterByTab(
        brands.list.data?.data ?? [],
        segments.list.data?.data ?? [],
      ),
    [brands.list.data?.data, segments.list.data?.data],
  );

  const [configuredBrand, setConfiguredBrand] = React.useState<BrandField>();
  const configuredBrandIndex = configuredBrand
    ? brandFields.findIndex(
        (field) => field.publicKey === configuredBrand.publicKey,
      )
    : undefined;

  const onSelectGlobalEanSpecification = (
    value: IBrandTemplateBrand['brandEanSpecification'],
  ) => {
    brandFields.forEach((field, index) => {
      if (!isNil(field.brandEanSpecification)) {
        methods.setValue(`brands.${index}.brandEanSpecification`, value);
      }
    });
  };

  const onSelectBrandEanSpecification = (
    value: IBrandTemplateBrand['brandEanSpecification'],
  ) => {
    if (!isNil(configuredBrandIndex)) {
      update(configuredBrandIndex, {
        ...methods.getValues(`brands.${configuredBrandIndex}`),
        brandEanSpecification: value,
      });
    }
  };

  useScrollToNewItem(brandFields, 'brand', 'name');

  const isStandard = config.variant === 'STANDARD';
  const brandsError = methods.formState.errors.brands as
    | { message?: string }
    | undefined;

  return (
    <Card className='mb-section'>
      <Card.Header>
        <Card.Title>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='team' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.brand_template.form.brands.title' />
            </span>
          </Title>
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col
            xs='7'
            md={configuredBrand ? 2 : 6}
            className={clsx('animate-slide', {
              'opacity-25': configuredBrand,
            })}
          >
            <AlphabeticalListSearchInput
              value={searchQuery}
              label={<Trans i18nKey='app.brand_template.form.brands.label' />}
              placeholder={t(
                'app.brand_template.list.table.search.placeholder',
              )}
              onChange={setSearchQuery}
            />
            <Tabs.Container defaultActiveKey='ALL'>
              <Tabs.List>
                {BrandTemplateHelpers.TABS.map((tab) => (
                  <Tabs.Item
                    key={tab.value}
                    eventKey={tab.value}
                    id={tab.value}
                  >
                    <Trans
                      i18nKey={tab.label}
                      values={{
                        count: Object.values(
                          brandsByLetterByTab[tab.value],
                        ).reduce(
                          (total, brandsByLetter) =>
                            total + brandsByLetter.length,
                          0,
                        ),
                      }}
                    />
                  </Tabs.Item>
                ))}
              </Tabs.List>

              <Tabs.Content>
                {BrandTemplateHelpers.TABS.map((tab) => (
                  <Tabs.Pane key={tab.value} eventKey={tab.value}>
                    <BrandAlphabeticalList
                      currentTab={tab.value}
                      searchQuery={searchQuery}
                      onAdd={handleOnAdd}
                      onRemove={handleOnRemove}
                      itemsByTab={brandsByLetterByTab[tab.value]}
                    />
                  </Tabs.Pane>
                ))}
              </Tabs.Content>
            </Tabs.Container>
          </Col>
          <Col
            xs='6'
            md={6}
            className='position-relative animate-slide'
            style={{ borderLeft: '1px solid var(--neutral-grey-4)' }}
          >
            {configuredBrand && (
              <div className='position-absolute' style={{ right: '105%' }}>
                <IconButton
                  id='close-configuration'
                  icon='arrow-left-s-line'
                  variant='secondary'
                  onClick={() => setConfiguredBrand(undefined)}
                  rounded
                />
              </div>
            )}
            {brandFields.length !== 0 ? (
              <>
                <Title heading='lg' weight='bold'>
                  <Trans
                    i18nKey='app.brand_template.form.active_brands.title'
                    values={{ count: brandFields.length }}
                  />
                </Title>
                {isStandard && (
                  <div className='mb-4'>
                    <SelectBrandEanConfiguration
                      name='brandEanSpecification'
                      onSelect={onSelectGlobalEanSpecification}
                    />
                    <DesadvGlobalCheckbox
                      onSelectAll={() => {
                        brandFields.forEach((_, index) => {
                          methods.setValue(`brands.${index}.desadv`, true);
                        });
                      }}
                      onDeselectAll={() => {
                        brandFields.forEach((_, index) => {
                          methods.setValue(`brands.${index}.desadv`, false);
                        });
                      }}
                    />
                  </div>
                )}
                <BrandDraggables
                  brands={brandFields}
                  onRemove={handleOnRemove}
                  onMove={handleOnMove}
                  onConfigure={isStandard ? setConfiguredBrand : undefined}
                  featureContext={config.featureContext}
                />
              </>
            ) : (
              <div className='text-center'>
                <Text
                  heading='sm'
                  weight='bold'
                  color='tertiary'
                  className='mb-1'
                >
                  <Trans i18nKey='app.brand_template.form.active_brands.empty.title' />
                </Text>
                <Text heading='sm' weight='normal' color='secondary-gray'>
                  <Trans i18nKey='app.brand_template.form.active_brands.empty.message' />
                </Text>
              </div>
            )}
            {brandsError?.message && (
              <FormFeedback invalid id='brands.error'>
                <Trans i18nKey={brandsError.message as keyof TranslationKeys} />
              </FormFeedback>
            )}
          </Col>
          {isStandard &&
            !isNil(configuredBrand) &&
            !isNil(configuredBrandIndex) && (
              <Col
                key={configuredBrandIndex}
                md={4}
                className='animate-slide'
                style={{ borderLeft: '1px solid var(--neutral-grey-4)' }}
              >
                <Title heading='lg' weight='bold' wrap>
                  <Trans
                    i18nKey='app.brand_template.form.brands.configure_brand'
                    values={{ brandName: configuredBrand.name }}
                  />
                </Title>
                <SelectBrandEanConfiguration
                  name={`brands.${configuredBrandIndex}.brandEanSpecification`}
                  onSelect={onSelectBrandEanSpecification}
                />
                <Text heading='sm' weight='light' color='primary'>
                  <Trans i18nKey='app.brand_template.form.brands.desadv_enabled.title' />
                </Text>
                <CheckboxField
                  label={
                    <Trans i18nKey='app.brand_template.form.brands.desadv_enabled.label' />
                  }
                  name={`brands.${configuredBrandIndex}.desadv`}
                  formGroupClassName=''
                  asTextField={false}
                />
                <hr />
                <CheckboxField
                  label={
                    <Trans i18nKey='app.brand_template.form.brands.recommended.label' />
                  }
                  name={`brands.${configuredBrandIndex}.recommended`}
                  formGroupClassName=''
                  asTextField={false}
                />
              </Col>
            )}
        </Row>
      </Card.Body>
    </Card>
  );
}
